import { useEffect, useState } from 'react';
import { AccountInfo } from '@azure/msal-browser';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { AuthService } from '../services/authService';

export interface UseAuthReturn {
  user: AccountInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

export const useAuth = (): UseAuthReturn => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<AccountInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authService] = useState(() => new AuthService(instance));

  useEffect(() => {
    if (isAuthenticated && accounts.length > 0) {
      setUser(accounts[0]);
    } else {
      setUser(null);
    }
    setIsLoading(false);
  }, [isAuthenticated, accounts]);

  const login = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await authService.login();
    } catch (error) {
      console.error('Login failed:', error);
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await authService.logout();
    } catch (error) {
      console.error('Logout failed:', error);
      setIsLoading(false);
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    if (!isAuthenticated || !user) {
      return null;
    }

    try {
      return await authService.getAccessToken(user);
    } catch (error) {
      console.error('Failed to get access token:', error);
      return null;
    }
  };

  return {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    getAccessToken,
  };
};
